import { z } from 'zod'
import { type ChequeStatus, receivedChequeInputSchema } from './cheques.js'
import {
  type Locale,
  localeSchema,
  moneySchema,
  optionalText,
  pageQuerySchema,
  quantitySchema,
  versionSchema,
} from './common.js'
import type { CostObjectProjectRef } from './cost-objects.js'
import type { TreasuryKind } from './finance.js'
import type { MessageErrorCode, MessageStatus } from './messaging.js'
import type { OrgUnitRef } from './org-units.js'
import type { PriceSource } from './pricing.js'
import { lineSerialsSchema } from './serials.js'

/**
 * The unit a product line is entered in (ADR-045): one of the product's packs, or its base unit. Omitted or
 * null: the base unit. Quantity and price are per this unit; stock moves quantity × the pack's factor.
 * Free-text lines take none.
 */
export const lineUnitIdSchema = z
  .uuid()
  .nullish()
  .transform((v) => v ?? null)

/** Line discount types: all three stay available on every document (settings G-D10). */
export const discountTypeSchema = z.enum(['PERCENTAGE', 'PER_UNIT', 'LINE_AMOUNT'])

// ── settings ───────────────────────────────────────────────────────────────────

export const salesSettingsSchema = z.object({
  pricesIncludeTax: z.boolean(),
  paymentTermsDays: z.number().int().min(0).max(365),
  /** Days a quotation created without a valid-until date stays valid (default 30). Left out: unchanged. */
  quotationValidDays: z.number().int().min(1).max(365).optional(),
  /**
   * The discount type new lines start with in the web's forms (default PER_UNIT); a line may still
   * use any of the three. Left out: unchanged.
   */
  defaultDiscountType: discountTypeSchema.optional(),
  version: versionSchema,
})
export type SalesSettingsUpdate = z.input<typeof salesSettingsSchema>

export interface SalesSettings {
  pricesIncludeTax: boolean
  paymentTermsDays: number
  version: number
  /** Days a quotation created without a valid-until date stays valid (1–365, default 30). */
  quotationValidDays: number
  /** The discount type new lines start with in the web's forms; a line may use any of the three. */
  defaultDiscountType: z.infer<typeof discountTypeSchema>
}

// ── invoices ───────────────────────────────────────────────────────────────────

export const invoiceLineInputSchema = z
  .object({
    variantId: z
      .uuid()
      .nullish()
      .transform((v) => v ?? null),
    /** Defaults to the product name for product lines; required for free-text lines. */
    description: optionalText(500),
    quantity: quantitySchema.refine((v) => Number(v) > 0, 'Quantity must be above zero'),
    unitId: lineUnitIdSchema,
    unitPrice: moneySchema.refine((v) => Number(v) >= 0, 'Price cannot be negative'),
    discountType: discountTypeSchema.default('PER_UNIT'),
    discountValue: moneySchema.default('0'),
    /** Omitted: the product's tax rate. null: no tax. A uuid: that rate. */
    taxRateId: z.uuid().nullable().optional(),
    /** One per unit for a serial-tracked product (exactly the quantity when posting); refused otherwise. */
    serials: lineSerialsSchema,
  })
  .refine((l) => l.variantId !== null || Boolean(l.description), {
    path: ['description'],
    message: 'Describe the line or choose a product',
  })
export type InvoiceLineInput = z.input<typeof invoiceLineInputSchema>

const invoiceFields = z.object({
  kind: z.enum(['INVOICE', 'DEBIT_NOTE']).default('INVOICE'),
  customerId: z.uuid(),
  invoiceDate: z.iso.date(),
  /** Defaults to invoice date + the tenant's payment terms. */
  dueDate: z.iso
    .date()
    .nullish()
    .transform((v) => v ?? null),
  warehouseId: z
    .uuid()
    .nullish()
    .transform((v) => v ?? null),
  reference: optionalText(100),
  notes: optionalText(2000),
  /**
   * The CUSTOMER project the document's revenue belongs to (projects/02 §3.3, header tag): it must be a
   * project of the document's customer (422 PROJECT_CUSTOMER_MISMATCH) and open; frozen once posted.
   * Omitted or null: none.
   */
  projectId: z
    .uuid()
    .nullish()
    .transform((v) => v ?? null),
  /**
   * The owning org unit (ADR-025, BR-SAL-019). Create: omitted = the creator's home unit, null = none.
   * Update: omitted keeps the current unit, only an explicit null clears it. A newly chosen unit must be
   * active; a creator whose scope is not ALL must choose one inside it.
   */
  orgUnitId: z.uuid().nullish(),
  /**
   * The salesperson credited with the sale (ADR-049): an active user. Create: omitted = the customer's
   * salesperson, null = none. Update: omitted keeps it, null clears it. Fixed once posted.
   */
  salespersonId: z.uuid().nullish(),
  lines: z.array(invoiceLineInputSchema).min(1, 'Add at least one line').max(500),
})
const dueNotBeforeInvoice = { path: ['dueDate'], message: 'The due date cannot be before the invoice date' }
const dueOk = (v: { invoiceDate: string; dueDate: string | null }) =>
  v.dueDate === null || v.dueDate >= v.invoiceDate
export const invoiceInputSchema = invoiceFields.refine(dueOk, dueNotBeforeInvoice)
export type InvoiceInput = z.input<typeof invoiceInputSchema>
export const invoiceUpdateSchema = invoiceFields
  .extend({ version: versionSchema })
  .refine(dueOk, dueNotBeforeInvoice)
export type InvoiceUpdate = z.input<typeof invoiceUpdateSchema>
/** Posting confirms the version the user reviewed. */
export const postInvoiceSchema = z.object({ version: versionSchema })

export type InvoiceStatus = 'DRAFT' | 'POSTED' | 'VOID'
export const paymentStatusSchema = z.enum(['UNPAID', 'PARTIAL', 'PAID', 'OVERDUE'])
export type PaymentStatus = z.infer<typeof paymentStatusSchema>

/**
 * `GET /sales/invoices`. `q` matches the number and reference (substring, case-insensitive), the
 * customer's code, and the customer's name and Arabic name with Arabic letter variants folded
 * (أ/إ/آ → ا, ة → ه, ى → ي; «مكتبه» finds «مكتبة»). User text is matched literally (no wildcards).
 * Sort keys: INVOICE_SORT_FIELDS (default `invoiceDate`, newest first with `-invoiceDate`).
 */
export const invoiceListQuerySchema = pageQuerySchema.extend({
  kind: z.enum(['INVOICE', 'DEBIT_NOTE']).optional(),
  status: z.enum(['DRAFT', 'POSTED', 'VOID']).optional(),
  /** Posted documents with an open balance. */
  open: z.stringbool().optional(),
  /**
   * Posted documents whose derived payment status (the list's `paymentStatus`) is this one; case does
   * not matter. OVERDUE: balance above zero and due date before today (Asia/Muscat). UNPAID / PARTIAL:
   * owed and not yet due, with nothing / something paid. PAID: nothing owed.
   */
  paymentStatus: z.string().trim().toUpperCase().pipe(paymentStatusSchema).optional(),
  customerId: z.uuid().optional(),
  from: z.iso.date().optional(),
  to: z.iso.date().optional(),
  /** Documents created by this user; `me` is the caller (the drafts queue, gap H-9). */
  createdBy: z.union([z.literal('me'), z.uuid()]).optional(),
  /** Documents tagged to this project. */
  projectId: z.uuid().optional(),
  /** Documents credited to this salesperson (ADR-049); `me` is the caller. */
  salespersonId: z.union([z.literal('me'), z.uuid()]).optional(),
})
export type InvoiceListQuery = z.input<typeof invoiceListQuerySchema>

/**
 * Sort keys of the invoice list and export. `balance` is the open balance (0 for drafts and void
 * documents); `customer` is the customer's name (alphabetical, Arabic included). Ties: newest first.
 */
export const INVOICE_SORT_FIELDS = [
  'invoiceDate',
  'dueDate',
  'number',
  'total',
  'balance',
  'customer',
] as const

/**
 * The invoice list's view tabs (pages/invoices.md §6.1), each the list call beside it:
 *   all      no status filter
 *   drafts   `status=DRAFT`
 *   open     `open=true` (posted, balance above zero; overdue included)
 *   overdue  `paymentStatus=OVERDUE`
 *   paid     `paymentStatus=PAID`
 *   void     `status=VOID`
 */
export const INVOICE_VIEWS = ['all', 'drafts', 'open', 'overdue', 'paid', 'void'] as const
export type InvoiceView = (typeof INVOICE_VIEWS)[number]

/**
 * `GET /sales/invoices/summary`: the list's filters other than the view (`status`, `open`,
 * `paymentStatus`), paging and sort. Unknown parameters are ignored.
 */
export const invoiceSummaryQuerySchema = invoiceListQuerySchema.pick({
  q: true,
  kind: true,
  customerId: true,
  from: true,
  to: true,
  createdBy: true,
  projectId: true,
})
export type InvoiceSummaryQuery = z.input<typeof invoiceSummaryQuerySchema>

/**
 * Counts per view for the current filters, from one grouped query. Each `count` equals the `total`
 * of the list call of that view with the same filters; `balance` (open and overdue only) is the sum
 * of the listed rows' `balance`. As of `asOf`, today in Asia/Muscat.
 */
export interface InvoiceSummary {
  asOf: string
  all: { count: number }
  drafts: { count: number }
  open: { count: number; balance: string }
  overdue: { count: number; balance: string }
  paid: { count: number }
  void: { count: number }
}

/** The most rows one invoice export returns; a larger filtered set is refused (422 `EXPORT_TOO_LARGE`). */
export const INVOICE_EXPORT_MAX_ROWS = 50_000

/**
 * `GET /sales/invoices/export`: the list's filters (view included: `status`, `open`, `paymentStatus`)
 * and sort, every matching row (no paging), as a CSV file (UTF-8 with a byte-order mark). `lang` picks
 * the header and the status labels.
 */
export const invoiceExportQuerySchema = invoiceListQuerySchema
  .omit({ page: true, pageSize: true })
  .extend({ format: z.enum(['csv']).default('csv'), lang: localeSchema.default('en') })
export type InvoiceExportQuery = z.input<typeof invoiceExportQuerySchema>

export interface CustomerRef {
  id: string
  code: string
  name: string
  nameAr: string | null
}

/**
 * The buyer's details a printed document carries (the tax invoice's buyer block), on the detail DTOs
 * of invoices, quotations, credit notes, receipts and refunds under `sales.read`: the print needs no
 * `customers.read`, and works while CRM is off (ADR-024). The same fields the public share page shows.
 */
export interface DocumentBuyer {
  taxNumber: string | null
  address: string | null
  city: string | null
  country: string | null
  mobile: string | null
  email: string | null
  /** The tenant's walk-in (cash) customer. */
  isWalkIn: boolean
}

/** A user named on a document (who created it). */
export interface UserRef {
  id: string
  name: string
}

export interface SalesInvoiceListItem {
  id: string
  kind: 'INVOICE' | 'DEBIT_NOTE'
  number: string | null
  status: InvoiceStatus
  paymentStatus: PaymentStatus | null
  customer: CustomerRef
  /**
   * Issued to the walk-in (cash) customer, i.e. a till sale: its goods come back through the till,
   * never as a credit note (ASM-028, ADR-024 D5). Always sent by the API; optional in the type
   * because older fixtures predate it.
   */
  walkIn?: boolean
  invoiceDate: string
  dueDate: string
  total: string
  amountPaid: string
  balance: string
  /** Days past the due date (today in Asia/Muscat) when `paymentStatus` is OVERDUE; null otherwise. */
  daysOverdue: number | null
  /** The user who created the document; null for migrated and system documents. */
  createdBy: UserRef | null
  /** The salesperson credited with the sale (ADR-049). Always sent; optional for older fixtures. */
  salesperson?: UserRef | null
  /** The project it is tagged to. Always sent by the API; optional in the type only for older fixtures. */
  project?: CostObjectProjectRef | null
  /**
   * Issued by Projects (billing plan, T&M; later claims): voided only from the project (422
   * PROJECT_DOCUMENT in Sales). Always sent by the API; optional in the type only for older fixtures.
   */
  issuedByProject?: boolean
  /**
   * Contracting (04 §9.3): the account the invoice credits instead of sales or service revenue, set by
   * the server for claims (`contract_revenue`), advances (`customer_advances`) and retention releases
   * (`retention_receivable`). Optional in the type only for older fixtures.
   */
  glRole?: 'contract_revenue' | 'customer_advances' | 'retention_receivable' | null
}

/**
 * One settlement of an invoice. To remove it (the source's credit returns to unallocated):
 * `DELETE /sales/receipts/:sourceId/allocations/:id` (receipts.create) for a RECEIPT,
 * `DELETE /sales/credit-notes/:sourceId/allocations/:id` (creditNotes.create) for a CREDIT_NOTE.
 */
export interface InvoiceAllocation {
  id: string
  sourceType: 'RECEIPT' | 'CREDIT_NOTE'
  sourceId: string
  sourceNumber: string
  amount: string
  allocatedOn: string
}

/** A priced document line (invoice, quotation). */
/** The unit of a line: its own (a pack or the base unit), or the product's base unit for older lines. */
export interface LineUnit {
  id: string
  symbol: string
  symbolAr: string | null
}

export interface DocumentLine {
  id: string
  lineNo: number
  variantId: string | null
  description: string
  quantity: string
  /** Null for a free-text line. Always sent by the API; optional in the type only for older fixtures. */
  unit?: LineUnit | null
  /** Base units per unit of the line ("12" for a carton of 12, "1" otherwise). Always sent; optional for fixtures. */
  unitFactor?: string
  /**
   * Sales documents: the level (or custom price) the line's price came from; null when the price was typed
   * by hand. Purchasing lines never carry one.
   */
  priceLevel?: PriceSource | null
  unitPrice: string
  discountType: 'PERCENTAGE' | 'PER_UNIT' | 'LINE_AMOUNT'
  discountValue: string
  taxRateId: string | null
  taxRate: string
  discount: string
  net: string
  tax: string
  total: string
}

export interface SalesInvoiceLine extends DocumentLine {
  /** Quantity already returned on posted credit notes. */
  returnedQuantity: string
  /** Serial numbers (serial-tracked products; empty otherwise). Always sent by the API; optional in the type only until the web fixtures carry it. */
  serials?: string[]
  /** The product tracks serial numbers: its lines carry one per unit. Always sent by the API; optional in the type only until the web fixtures carry it. */
  trackSerials?: boolean
}

export interface SalesInvoice extends SalesInvoiceListItem {
  /** The buyer block for the print (detail only; optional in the type because test fixtures predate it). */
  buyer?: DocumentBuyer
  warehouse: { id: string; code: string } | null
  pricesIncludeTax: boolean
  reference: string | null
  notes: string | null
  gross: string
  discount: string
  subtotal: string
  tax: string
  lines: SalesInvoiceLine[]
  /** Settlements: receipts and credit notes applied to this document. */
  allocations: InvoiceAllocation[]
  journalEntryId: string | null
  /** The quotation converted into this document (`quotations.converted_invoice_id`), or null. */
  quotation: { id: string; number: string } | null
  /** The till sale this invoice was issued for (`pos_sales.invoice_id`), or null. */
  posSaleId: string | null
  /**
   * The owning org unit (ADR-025); null when none. Always sent by the API; optional in the type only so
   * older fixtures still compile.
   */
  orgUnit?: OrgUnitRef | null
  voidReason: string | null
  version: number
  createdAt: string
  updatedAt: string
}

/**
 * `GET /sales/invoices/:id/stock-check` (invoices.post), a DRAFT's goods lines whose quantity exceeds
 * what is on hand at the draft's warehouse (the main warehouse when none is chosen), summed per
 * variant. An empty array means every line is covered. Under the negative-stock policy (`inventory`
 * setting group, settings Q4): WARN — advice, posting proceeds; BLOCK — the same shortages refuse
 * posting (`InsufficientStockProblem`); ALLOW — always empty (no warning).
 */
export interface InvoiceStockShortage {
  variantId: string
  sku: string | null
  /** Product name, with the variant's name after " — " when it has one. */
  name: string
  /** Σ quantity of the draft's lines for this variant. */
  required: string
  /** On hand at the warehouse now (may be negative). */
  onHand: string
}

/**
 * 422 `INSUFFICIENT_STOCK` (problem document): the negative-stock policy is BLOCK and posting an
 * invoice or a till sale would take these variants below zero at `warehouseId`. Nothing was posted.
 */
export interface InsufficientStockProblem {
  status: 422
  code: 'INSUFFICIENT_STOCK'
  detail: string
  warehouseId: string
  shortages: InvoiceStockShortage[]
  /** Inventory is switched off: the policy still applies, and only Settings can change it (ADR-024 D6). */
  inventoryOff?: true
}

// ── invoice e-mail (R0b MS8) ──────────────────────────────────────────────────

const recipientSchema = z.string().trim().toLowerCase().pipe(z.email().max(254))

/**
 * POST /sales/invoices/:id/email (`invoices.send`): the invoice's PDF in `locale`, one message per
 * recipient (To and Cc alike). `requestId` is fresh per dialog opening, so a double click sends once.
 */
export const sendInvoiceEmailSchema = z
  .object({
    to: z.array(recipientSchema).min(1).max(5),
    cc: z.array(recipientSchema).max(5).default([]),
    locale: localeSchema,
    note: z.string().trim().max(500).nullish(),
    requestId: z.uuid(),
  })
  .strict()
export type SendInvoiceEmail = z.input<typeof sendInvoiceEmailSchema>

/** 202 from POST /sales/invoices/:id/email. */
export interface SendInvoiceEmailResult {
  messages: { id: string; recipientMasked: string | null }[]
  issuedDocumentId: string
}

/** GET /sales/invoices/:id/messages (`sales.read`): the e-mails sent about the invoice, newest first. */
export interface InvoiceMessage {
  id: string
  status: MessageStatus
  locale: Locale
  recipientMasked: string | null
  /** Why it failed or was suppressed. */
  lastErrorCode: MessageErrorCode | null
  issuedDocumentId: string | null
  createdAt: string
  sentAt: string | null
  createdBy: { id: string; name: string } | null
}

// ── receipts ───────────────────────────────────────────────────────────────────

export const PAYMENT_METHODS = ['CASH', 'CARD', 'BANK_TRANSFER', 'CHEQUE', 'OTHER'] as const
export const paymentMethodSchema = z.enum(PAYMENT_METHODS)
export type PaymentMethod = z.infer<typeof paymentMethodSchema>

/**
 * One of the company's own payment methods (Visa, Thawani, Bank Muscat transfer; ADR-047) on a document.
 * The document's `method` stays its type, which the accounting rules read; null: the plain type.
 */
export const paymentMethodIdSchema = z
  .uuid()
  .transform((v) => v.toLowerCase())
  .nullish()
  .transform((v) => v ?? null)

/** A company payment method as documents show it. */
export interface PaymentMethodRef {
  id: string
  name: string
  nameAr: string | null
}

/**
 * Whether money paid by `method` can move through an account of `kind`. Cash moves only through a
 * cash account, and a cash account holds nothing a drawer cannot: card slips and transfers settle
 * in a terminal or bank account (a cheque may wait in the cash box until it is banked). Receipts,
 * refunds and supplier payments refuse a method that does not fit (422 on `method`).
 */
export function methodFitsAccount(method: PaymentMethod, kind: TreasuryKind): boolean {
  if (method === 'CASH') return kind === 'CASH'
  if (kind === 'CASH') return method === 'CHEQUE' || method === 'OTHER'
  return true
}

/** The usual way money moves through each kind of account; it always fits the kind. */
export const USUAL_METHOD: Record<TreasuryKind, PaymentMethod> = {
  CASH: 'CASH',
  BANK: 'BANK_TRANSFER',
  CARD_TERMINAL: 'CARD',
  MOBILE_WALLET: 'OTHER',
}

export const allocationInputSchema = z.object({
  invoiceId: z.uuid(),
  amount: moneySchema.refine((v) => Number(v) > 0, 'Enter an amount above zero'),
})

export const receiptInputSchema = z.object({
  customerId: z.uuid(),
  receiptDate: z.iso.date(),
  treasuryAccountId: z.uuid(),
  method: paymentMethodSchema,
  /** The company's own method (its type must be `method`); null: the plain type. */
  paymentMethodId: paymentMethodIdSchema,
  amount: moneySchema.refine((v) => Number(v) > 0, 'Enter an amount above zero'),
  reference: optionalText(100),
  notes: optionalText(500),
  allocations: z.array(allocationInputSchema).max(200).default([]),
  /**
   * The owning org unit (ADR-025, BR-SAL-019): omitted = the creator's home unit, null = none. It must be
   * active; a creator whose scope is not ALL must choose one inside it.
   */
  orgUnitId: z.uuid().nullish(),
  /**
   * Method CHEQUE (ADR-046): the cheque's details, so it is followed up until the bank pays it (the receipt
   * then debits "Cheques under collection", not the account). Left out: the cheque counts as paid into the
   * account at once. Refused with any other method.
   */
  cheque: receivedChequeInputSchema.nullish().transform((v) => v ?? null),
})
export type ReceiptInput = z.input<typeof receiptInputSchema>

/** The cheque of a receipt or a supplier payment, as their pages show it. */
export interface DocumentChequeRef {
  id: string
  number: string
  bankName: string
  chequeDate: string
  status: ChequeStatus
}

export const allocateSchema = z.object({ allocations: z.array(allocationInputSchema).min(1).max(200) })
export type AllocateInput = z.input<typeof allocateSchema>

/**
 * `GET /sales/receipts`. `q` matches the number and reference, the customer's code, and the customer's
 * name and Arabic name with Arabic letter variants folded (as the invoice list). Sort keys:
 * RECEIPT_SORT_FIELDS (default `receiptDate`).
 */
export const receiptListQuerySchema = pageQuerySchema.extend({
  customerId: z.uuid().optional(),
  status: z.enum(['POSTED', 'VOID']).optional(),
  from: z.iso.date().optional(),
  to: z.iso.date().optional(),
  /** Posted receipts with credit left to refund or allocate (amount − allocated − refunded > 0). */
  hasOpenCredit: z.stringbool().optional(),
  /** Receipts taken by this method. */
  method: paymentMethodSchema.optional(),
  /** Receipts paid into this cash or bank account (the daily cash-up: today, account = the till). */
  treasuryAccountId: z.uuid().optional(),
  /** Receipts recorded by this user; `me` is the caller. */
  createdBy: z.union([z.literal('me'), z.uuid()]).optional(),
})
export type ReceiptListQuery = z.input<typeof receiptListQuerySchema>

/**
 * Sort keys of the receipt list and export. `unallocated` is the credit left (0 for void receipts);
 * `customer` the customer's name. Ties: newest first.
 */
export const RECEIPT_SORT_FIELDS = ['receiptDate', 'number', 'amount', 'unallocated', 'customer'] as const

/**
 * The receipt list's view tabs (pages/sales-documents.md §1.6), each the list call beside it:
 *   all         no view filter
 *   withCredit  `hasOpenCredit=true` (suggested sort `receiptDate`, oldest credit first)
 *   void        `status=VOID`
 */
export const RECEIPT_VIEWS = ['all', 'withCredit', 'void'] as const
export type ReceiptView = (typeof RECEIPT_VIEWS)[number]

/** `GET /sales/receipts/summary`: the list's filters other than the view, paging and sort. */
export const receiptSummaryQuerySchema = receiptListQuerySchema.pick({
  q: true,
  customerId: true,
  from: true,
  to: true,
  method: true,
  treasuryAccountId: true,
  createdBy: true,
})
export type ReceiptSummaryQuery = z.input<typeof receiptSummaryQuerySchema>

/**
 * Counts per view for the current filters (one grouped query); each `count` equals the `total` of that
 * view's list call. `all.amount` is Σ amount of the POSTED receipts among them (a void receipt received
 * nothing); `withCredit.unallocated` is Σ `unallocated` of that view's rows.
 */
export interface ReceiptSummary {
  all: { count: number; amount: string }
  withCredit: { count: number; unallocated: string }
  void: { count: number }
}

/** `GET /sales/receipts/export` (sales.export): the list's filters, view and sort, as CSV. */
export const receiptExportQuerySchema = receiptListQuerySchema
  .omit({ page: true, pageSize: true })
  .extend({ format: z.enum(['csv']).default('csv'), lang: localeSchema.default('en') })
export type ReceiptExportQuery = z.input<typeof receiptExportQuerySchema>

/**
 * The most rows one export of a sales document list returns (receipts, quotations, credit notes,
 * refunds); a larger filtered set is refused (422 `EXPORT_TOO_LARGE`).
 */
export const SALES_DOCUMENT_EXPORT_MAX_ROWS = 50_000

/**
 * A refund drawn from a receipt or credit note (the document trail). `amount` is what the refund took
 * from this source, not the refund's total. A void refund has given its credit back, so it is never
 * listed.
 */
export interface DrawnRefund {
  id: string
  number: string
  refundDate: string
  amount: string
  status: 'POSTED' | 'VOID'
}

export interface Receipt {
  id: string
  number: string
  receiptDate: string
  customer: CustomerRef
  treasuryAccount: { id: string; name: string }
  method: PaymentMethod
  /** The company's own method it was taken by (ADR-047), or null. Optional only for older fixtures. */
  paymentMethod?: PaymentMethodRef | null
  amount: string
  allocated: string
  /** Paid back to the customer through refunds. */
  refunded: string
  /** The customer's credit left on this receipt: amount − allocated − refunded. */
  unallocated: string
  reference: string | null
  notes: string | null
  status: 'POSTED' | 'VOID'
  voidReason: string | null
  /** Taken at the till (a sale's tender): undone only by voiding the sale (`TILL_DOCUMENT`). */
  fromTill: boolean
  /** The cheque it was paid with, followed up (ADR-046). Always sent by the API; optional for older fixtures. */
  cheque?: DocumentChequeRef | null
  /**
   * The invoices and debit notes this receipt settles. `invoiceDate` and `invoiceKind` (detail only)
   * are what the receipt voucher prints beside the number.
   */
  allocations: {
    id: string
    invoiceId: string
    invoiceNumber: string
    amount: string
    allocatedOn: string
    invoiceDate: string
    invoiceKind: 'INVOICE' | 'DEBIT_NOTE'
  }[]
  createdAt: string
  /**
   * The user who recorded the receipt (the voucher's "Received by"); null for migrated and system
   * receipts (list and detail).
   */
  createdBy: UserRef | null
  /**
   * The receipt's journal entry while it stands; null once voided (SD-63). Detail only: optional
   * because the list's rows share this type.
   */
  journalEntryId?: string | null
  /** Refunds that paid this receipt's credit back (the trail; RC-H4). Detail only (as above), oldest first. */
  refunds?: DrawnRefund[]
  /** The buyer block for the voucher. Detail only (as above). */
  buyer?: DocumentBuyer
  /** The owning org unit (ADR-025); null when none. Detail only (as above). */
  orgUnit?: OrgUnitRef | null
}

// ── customer refunds ───────────────────────────────────────────────────────────

/** Where refunded credit comes from: a posted receipt's or credit note's unapplied remainder. */
export const creditSourceTypeSchema = z.enum(['RECEIPT', 'CREDIT_NOTE'])
export type CreditSourceType = z.infer<typeof creditSourceTypeSchema>

export const refundApplicationInputSchema = z.object({
  sourceType: creditSourceTypeSchema,
  sourceId: z.uuid(),
  amount: moneySchema.refine((v) => Number(v) > 0, 'Enter an amount above zero'),
})

/**
 * The refund's amount is the sum of the credit it consumes. Paid by cheque, `reference` is the cheque's
 * number and is required: a refund cheque stays outside the cheque register (ASM-254), so the reference is
 * all that names it.
 */
export const refundInputSchema = z
  .object({
    customerId: z.uuid(),
    refundDate: z.iso.date(),
    treasuryAccountId: z.uuid(),
    method: paymentMethodSchema,
    paymentMethodId: paymentMethodIdSchema,
    reference: optionalText(100),
    notes: optionalText(500),
    applications: z.array(refundApplicationInputSchema).min(1, 'Choose the credit to refund').max(200),
    /**
     * The owning org unit (ADR-025, BR-SAL-019): omitted = the creator's home unit, null = none. It must be
     * active; a creator whose scope is not ALL must choose one inside it.
     */
    orgUnitId: z.uuid().nullish(),
  })
  .superRefine((v, ctx) => {
    if (v.method === 'CHEQUE' && !v.reference) {
      ctx.addIssue({ code: 'custom', path: ['reference'], message: 'Enter the cheque number' })
    }
  })
export type RefundInput = z.input<typeof refundInputSchema>

/**
 * `GET /sales/refunds`. `q` matches the number and reference, the customer's code, and the customer's
 * name and Arabic name with Arabic letter variants folded (the shared document search, SD-64). Sort
 * keys: REFUND_SORT_FIELDS (default `refundDate`).
 */
export const refundListQuerySchema = pageQuerySchema.extend({
  customerId: z.uuid().optional(),
  status: z.enum(['POSTED', 'VOID']).optional(),
  from: z.iso.date().optional(),
  to: z.iso.date().optional(),
  /** Refunds paid by this method. */
  method: paymentMethodSchema.optional(),
  /** Refunds paid from this cash or bank account. */
  treasuryAccountId: z.uuid().optional(),
  /** Refunds recorded by this user; `me` is the caller. */
  createdBy: z.union([z.literal('me'), z.uuid()]).optional(),
})
export type RefundListQuery = z.input<typeof refundListQuerySchema>

/** Sort keys of the refund list and export (`customer`: the customer's name). Ties: newest first. */
export const REFUND_SORT_FIELDS = ['refundDate', 'number', 'amount', 'customer'] as const

/**
 * The refund list's view tabs (pages/sales-documents.md §4.6):
 *   all   no view filter
 *   void  `status=VOID`
 */
export const REFUND_VIEWS = ['all', 'void'] as const
export type RefundView = (typeof REFUND_VIEWS)[number]

/** `GET /sales/refunds/summary`: the list's filters other than the view (`status`), paging and sort. */
export const refundSummaryQuerySchema = refundListQuerySchema.pick({
  q: true,
  customerId: true,
  from: true,
  to: true,
  method: true,
  treasuryAccountId: true,
  createdBy: true,
})
export type RefundSummaryQuery = z.input<typeof refundSummaryQuerySchema>

/**
 * Counts per view for the current filters; each `count` equals the `total` of that view's list call.
 * `all.amount` is Σ amount of the POSTED refunds among them (a void refund paid nothing out).
 */
export interface RefundSummary {
  all: { count: number; amount: string }
  void: { count: number }
}

/** `GET /sales/refunds/export` (sales.export): the list's filters, view and sort, as CSV. */
export const refundExportQuerySchema = refundListQuerySchema
  .omit({ page: true, pageSize: true })
  .extend({ format: z.enum(['csv']).default('csv'), lang: localeSchema.default('en') })
export type RefundExportQuery = z.input<typeof refundExportQuerySchema>

export interface CustomerRefundListItem {
  id: string
  /** REF-2026-0001 */
  number: string
  refundDate: string
  customer: CustomerRef
  treasuryAccount: { id: string; name: string }
  method: PaymentMethod
  /** The company's own method it was paid by (ADR-047), or null. Optional only for older fixtures. */
  paymentMethod?: PaymentMethodRef | null
  amount: string
  reference: string | null
  status: 'POSTED' | 'VOID'
  /** Paid out at the till (a till return): cannot be voided from the back office (`TILL_DOCUMENT`). */
  fromTill: boolean
  /**
   * The user who recorded the refund (the payment voucher's "Paid by"); null for migrated and system
   * refunds.
   */
  createdBy: UserRef | null
  /**
   * Where the money came from, for the list's "From" column (RF-H2): the receipts and credit notes
   * it drew on, oldest application first. Empty for a void refund (its credit went back). List only;
   * the detail has `applications`.
   */
  sources?: { sourceType: CreditSourceType; sourceId: string; sourceNumber: string; amount: string }[]
}

export interface CustomerRefund extends CustomerRefundListItem {
  /** The buyer block for the print (detail only; optional in the type because test fixtures predate it). */
  buyer?: DocumentBuyer
  /** The owning org unit (ADR-025); null when none. Optional in the type only for older fixtures. */
  orgUnit?: OrgUnitRef | null
  notes: string | null
  voidReason: string | null
  applications: {
    id: string
    sourceType: CreditSourceType
    sourceId: string
    sourceNumber: string
    amount: string
  }[]
  journalEntryId: string | null
  createdAt: string
}

// ── receivables reports ────────────────────────────────────────────────────────

/** A statement period (customer or supplier): `to` may not precede `from` (422, like the account statement). */
export const statementQuerySchema = z
  .object({ from: z.iso.date(), to: z.iso.date() })
  .superRefine((q, ctx) => {
    if (q.to < q.from) {
      ctx.addIssue({ code: 'custom', path: ['to'], message: 'The end date must be after the start date' })
    }
  })

/**
 * What a customer statement line comes from. Every receivables posting is one of these documents
 * (manual journals cannot touch the receivables account); OTHER is a safety net for a future source.
 * The opening balance is the statement's `opening` figure, never a line.
 */
export const CUSTOMER_STATEMENT_SOURCE_KINDS = [
  'INVOICE',
  'DEBIT_NOTE',
  'RECEIPT',
  'CREDIT_NOTE',
  'REFUND',
  'OTHER',
] as const
export type CustomerStatementSourceKind = (typeof CUSTOMER_STATEMENT_SOURCE_KINDS)[number]

export interface CustomerStatement {
  customer: CustomerRef
  from: string
  to: string
  opening: string
  lines: {
    date: string
    journalEntryId: string
    journalNumber: string
    sourceType: string | null
    sourceId: string | null
    /** Stable kind of the source document, for localized descriptions. */
    sourceKind: CustomerStatementSourceKind
    /** The source document's number (INV-001, PAY-2026-0001, CN-000001, REF-2026-0001…). */
    sourceNumber: string | null
    /** The line reverses an earlier one: its source document was voided. */
    reversal: boolean
    /** Built by the server in English; the UI should prefer `sourceKind` + `sourceNumber`. */
    description: string | null
    debit: string
    credit: string
    balance: string
  }[]
  closing: string
}

export const agingQuerySchema = z.object({ asOf: z.iso.date() })

export interface AgingRow {
  customer: CustomerRef
  current: string
  days1to30: string
  days31to60: string
  days61to90: string
  over90: string
  /** Open invoice balances minus unallocated credits. */
  total: string
  unallocatedCredit: string
}

// ── customer account summary (profile) ────────────────────────────────────────

export type AgingBucket = 'days1to30' | 'days31to60' | 'days61to90' | 'over90'

/**
 * `GET /customers/:id/summary` (sales.read): the profile's key figures, tab counts and "needs
 * attention" counts in one call. Figures are as of `asOf`, today in Asia/Muscat. Each count equals the
 * `total` of the list call named beside it, so a figure and the tab it opens always agree.
 */
export interface CustomerAccountSummary {
  customer: CustomerRef
  asOf: string
  /** The aging total = the statement closing balance: open invoices and debit notes − unallocated credit. */
  balance: string
  /** Open invoice and debit note balances past their due date (before credit). */
  overdue: string
  /** The oldest aging bucket holding an overdue amount; null when nothing is overdue. */
  oldestOverdueBucket: AgingBucket | null
  /** Credit left on posted receipts and credit notes (not allocated, not refunded). */
  unallocatedCredit: string
  /** Latest posted invoice by invoice date (kind INVOICE; debit notes are not counted). */
  lastInvoice: { id: string; number: string; invoiceDate: string; total: string } | null
  /** Latest posted receipt by receipt date. */
  lastReceipt: { id: string; number: string; receiptDate: string; amount: string } | null
  /**
   * From the first day of the fiscal year containing `asOf` (1 January when no year is defined) to
   * `asOf`: ex-VAT, net of credit notes. Equals `GET /reports/sales-by-customer?from&to&customerId`.
   */
  salesThisYear: { from: string; to: string; netSales: string; invoiceCount: number }
  counts: {
    invoices: {
      /** `/sales/invoices?customerId` (invoices and debit notes, every status). */
      all: number
      /** `…&open=true` */
      open: number
      /** `…&paymentStatus=OVERDUE` */
      overdue: number
      /** `…&status=DRAFT` */
      draft: number
    }
    /** `/sales/receipts?customerId`; `openCredit`: `…&hasOpenCredit=true`. */
    receipts: { all: number; openCredit: number }
    /** `/sales/credit-notes?customerId`; `draft`: `…&status=DRAFT`; `openCredit`: `…&hasOpenCredit=true`. */
    creditNotes: { all: number; draft: number; openCredit: number }
    /**
     * `/sales/quotations?customerId`; `pending`: `…&status=PENDING`; `expired`: pending or approved
     * quotations past their valid-until date (the list's derived `expired` marker; no status change).
     */
    quotations: { all: number; pending: number; expired: number }
    /** `/sales/refunds?customerId`. */
    refunds: number
  }
  attention: {
    overdueInvoices: number
    /** Draft invoices and debit notes. */
    draftInvoices: number
    draftCreditNotes: number
    pendingQuotations: number
    /** Receipts and credit notes with credit left to allocate or refund. */
    openCreditSources: number
  }
}
